import { useEffect, useState, type CSSProperties, type PointerEvent as ReactPointerEvent } from 'react'
import { NavLink } from 'react-router-dom'
import clsx from 'clsx'
import { Cpu, FolderKanban, ImageIcon, Settings, Shield, X } from 'lucide-react'
import { BrandLockup } from '@/components/BrandLogo'
import { useAuth } from '@/store/auth'
// Everything the hidden conversation list needed. Left here so putting
// chat back is uncommenting, not rewriting.
// import { useMemo } from 'react'
// import { useNavigate } from 'react-router-dom'
// import { MessageSquarePlus, Search, Trash2 } from 'lucide-react'
// import { useChat } from '@/store/chat'
// import type { Conversation } from '@/services/types'

/**
 * The application's own navigation.
 *
 * <p>From `lg` up it is a column of the layout. Below that there is not enough
 * width for a 288px rail and a usable conversation beside it, so it becomes a
 * drawer over the content and `open` decides whether it is showing.
 */
const WIDTH_KEY = 'nevtan.sidebarWidth'
const WIDTH_DEFAULT = 288
const WIDTH_MIN = 216
const WIDTH_MAX = 460

/** The remembered width, clamped — a stored value from another build must not
 *  be able to leave the sidebar unusably narrow or eat the conversation. */
function storedWidth(): number {
  try {
    const raw = Number(localStorage.getItem(WIDTH_KEY))
    if (!raw) return WIDTH_DEFAULT
    return Math.min(WIDTH_MAX, Math.max(WIDTH_MIN, raw))
  } catch {
    return WIDTH_DEFAULT
  }
}

export function Sidebar({ open = false, onClose }: { open?: boolean; onClose?: () => void }) {
  const user = useAuth((state) => state.user)
  // const navigate = useNavigate()
  // const { conversations, activeId, startNewChat, deleteConversation } = useChat()
  // const [query, setQuery] = useState('')
  const [width, setWidth] = useState(storedWidth)

  // Takes an updater as well as a value: a held arrow key fires faster than
  // React re-renders, and reading `width` from the closure would then throw
  // away every press but the first.
  const commitWidth = (next: number | ((prev: number) => number)) => {
    setWidth((prev) => {
      const value = typeof next === 'function' ? next(prev) : next
      return Math.min(WIDTH_MAX, Math.max(WIDTH_MIN, value))
    })
  }

  // Persisted from an effect rather than inside the updater, which React may
  // run more than once per change.
  useEffect(() => {
    try {
      localStorage.setItem(WIDTH_KEY, String(width))
    } catch {
      // The width still applies for this session.
    }
  }, [width])

  // Pointer events rather than mouse: the same handler then covers a trackpad,
  // a mouse and a stylus. Capture keeps the drag alive if the pointer outruns
  // the 4px handle, which at speed it always does.
  const startDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    event.preventDefault()
    const handle = event.currentTarget
    handle.setPointerCapture(event.pointerId)
    const move = (e: PointerEvent) => commitWidth(e.clientX)
    const stop = () => {
      handle.releasePointerCapture(event.pointerId)
      handle.removeEventListener('pointermove', move)
      handle.removeEventListener('pointerup', stop)
      document.body.style.userSelect = ''
    }
    // Without this the drag selects the conversation titles it passes over.
    document.body.style.userSelect = 'none'
    handle.addEventListener('pointermove', move)
    handle.addEventListener('pointerup', stop)
  }

  // Filtering locally keeps search instant; the API also supports ?q= for
  // conversations that have scrolled out of the loaded set.
  // const filtered = useMemo(() => {
  //   const needle = query.trim().toLowerCase()
  //   if (!needle) return conversations
  //   return conversations.filter((c) => c.title.toLowerCase().includes(needle))
  // }, [conversations, query])

  // const groups = useMemo(() => groupByDay(filtered), [filtered])

  return (
    <>
      {/* Dims and closes the drawer. Only below lg, where the drawer exists. */}
      {open && (
        <button
          type="button"
          onClick={onClose}
          aria-label="Close navigation"
          className="fixed inset-0 z-30 bg-black/40 lg:hidden"
        />
      )}

      <aside
        // The chosen width applies only from lg, where the sidebar is a column
        // of the layout. As a drawer it always takes its own fixed width.
        style={{ '--nv-sidebar': `${width}px` } as CSSProperties}
        className={clsx(
          'flex w-72 shrink-0 flex-col border-r border-border bg-surface-raised lg:w-[var(--nv-sidebar)]',
          // Below lg it floats over the content; from lg it is simply a column.
          'fixed inset-y-0 left-0 z-40 transition-transform duration-200 lg:relative lg:z-auto lg:translate-x-0',
          open ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        {/* Drag to resize. Only from lg: a drawer is dismissed, not resized. */}
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize sidebar"
          aria-valuenow={width}
          aria-valuemin={WIDTH_MIN}
          aria-valuemax={WIDTH_MAX}
          tabIndex={0}
          onPointerDown={startDrag}
          onDoubleClick={() => commitWidth(WIDTH_DEFAULT)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowLeft') commitWidth((w) => w - 16)
            else if (e.key === 'ArrowRight') commitWidth((w) => w + 16)
            else if (e.key === 'Home') commitWidth(WIDTH_DEFAULT)
            else return
            e.preventDefault()
          }}
          title="Drag to resize · double-click to reset"
          className="absolute inset-y-0 -right-1 z-10 hidden w-2 cursor-col-resize touch-none lg:block"
        >
          <span className="pointer-events-none absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-transparent transition hover:bg-accent" />
        </div>

        <div className="flex items-center gap-1 px-4 py-4">
          {/* The sidebar follows the theme, so the lockup does too: teal on the
              light surface, white on the dark one. */}
          <BrandLockup markClassName="h-8 w-8" textClassName="text-[18px]" />
          <button
            type="button"
            onClick={onClose}
            aria-label="Close navigation"
            className="ml-auto rounded-lg p-1.5 text-content-muted hover:text-content lg:hidden"
          >
            <X size={18} />
          </button>
        </div>

      {/*
        Chat has moved to the desktop app, so the New chat button, the
        conversation search and the conversation list are all hidden. The
        code is left whole rather than removed: putting chat back on the
        website is uncommenting this block and the two routes in App.tsx.

        The links below stay -- Models, Projects and Settings are still
        served here.
      */}
      {/*
      <div className="space-y-2 px-3">
        <button
          type="button"
          onClick={() => {
            // Goes to the blank chat rather than creating a conversation up
            // front, so clicking this repeatedly does not leave a trail of
            // empty conversations in the sidebar. The first message creates it.
            startNewChat()
            navigate('/chat')
          }}
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-accent px-3 py-2 text-sm font-semibold text-accent-content shadow-raised transition hover:opacity-90"
        >
          <MessageSquarePlus size={16} />
          New chat
        </button>

        <div className="flex items-center gap-2 rounded-lg bg-surface-sunken px-3 py-2">
          <Search size={14} className="text-content-muted" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search conversations"
            className="w-full bg-transparent text-sm outline-none placeholder:text-content-muted"
          />
        </div>
      </div>

      <nav className="mt-4 flex-1 overflow-y-auto px-3 pb-4">
        {groups.map(([label, items]) => (
          <div key={label} className="mb-4">
            <div className="px-2 pb-1 text-[11px] font-medium uppercase tracking-wide text-content-muted">
              {label}
            </div>
            <ul className="space-y-0.5">
              {items.map((conversation) => (
                <li key={conversation.id} className="group relative">
                  <NavLink
                    to={`/chat/${conversation.id}`}
                    className={clsx(
                      'block truncate rounded-lg px-2 py-1.5 pr-8 text-sm',
                      conversation.id === activeId
                        ? 'bg-accent/10 font-medium text-accent'
                        : 'text-content-muted hover:bg-surface-sunken hover:text-content',
                    )}
                  >
                    {conversation.title}
                  </NavLink>
                  <button
                    type="button"
                    onClick={async () => {
                      await deleteConversation(conversation.id)
                      if (conversation.id === activeId) navigate('/chat')
                    }}
                    className="absolute right-1.5 top-1.5 hidden rounded p-1 text-content-muted hover:text-danger group-hover:block"
                    aria-label={`Delete ${conversation.title}`}
                  >
                    <Trash2 size={13} />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ))}

        {filtered.length === 0 && (
          <p className="px-2 text-sm text-content-muted">
            {query ? 'No conversations match that search.' : 'No conversations yet.'}
          </p>
        )}
      </nav>
      */}

        {/* The account, its theme and the suite switcher all live in the
            header now, so this stays purely navigation. */}
        <div className="space-y-0.5 border-t border-border p-3">
          <SidebarLink to="/models" icon={<Cpu size={16} />} label="Models" />
          <SidebarLink to="/images" icon={<ImageIcon size={16} />} label="Images" />
          <SidebarLink to="/projects" icon={<FolderKanban size={16} />} label="Projects" />
          <SidebarLink to="/settings" icon={<Settings size={16} />} label="Settings" />
          {/* Shown only to administrators. The page and the server both refuse
              anyone else; this keeps a link that answers "not for you" out of
              everyone else's sidebar. */}
          {user?.role === 'ADMIN' && (
            <SidebarLink to="/admin" icon={<Shield size={16} />} label="Administration" />
          )}
        </div>
      </aside>
    </>
  )
}

function SidebarLink({ to, icon, label }: { to: string; icon: React.ReactNode; label: string }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        clsx(
          'flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm',
          isActive ? 'bg-surface-sunken font-medium' : 'text-content-muted hover:text-content',
        )
      }
    >
      {icon}
      {label}
    </NavLink>
  )
}

// Served only the hidden conversation list.
// /** Groups conversations into Today / Yesterday / Previous 7 days / Older. */
// function groupByDay(conversations: Conversation[]): [string, Conversation[]][] {
//   const now = Date.now()
//   const day = 24 * 60 * 60 * 1000
//   const buckets = new Map<string, Conversation[]>([
//     ['Today', []],
//     ['Yesterday', []],
//     ['Previous 7 days', []],
//     ['Older', []],
//   ])
//
//   for (const conversation of conversations) {
//     const age = now - new Date(conversation.updatedAt).getTime()
//     const label = age < day ? 'Today' : age < 2 * day ? 'Yesterday' : age < 7 * day ? 'Previous 7 days' : 'Older'
//     buckets.get(label)!.push(conversation)
//   }
//
//   return [...buckets.entries()].filter(([, items]) => items.length > 0)
// }
